"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

const faqs = [
  {
    id: "faq-1",
    q: "Do I need to buy NFC tags separately?",
    a: "No! The Starter Kit includes 5 premium NFC tags plus your first month of service for $49. If you're on a monthly plan, you can purchase additional tags directly through your dashboard (pricing varies by tier: $4/tag on Starter, $3/tag on Pro, $2/tag on Collector).",
  },
  {
    id: "faq-2",
    q: "Can I use the free tier with NFC tags?",
    a: "No. The free tier is a digital preview that lets you catalog up to 5 items and test TapTale's features. To activate and use NFC tags, you need an active paid subscription (Starter, Pro, or Collector). We recommend starting with the Starter Kit to get everything you need in one purchase.",
  },
  {
    id: "faq-3",
    q: "What happens if I cancel my subscription?",
    a: "Your NFC tags will deactivate and stop working. Your digital catalog remains accessible in read-only mode, but you won't be able to edit items or activate tags. You can reactivate anytime by resubscribing — your data is never deleted.",
  },
  {
    id: "faq-4",
    q: "What's the difference between Starter Kit and Starter subscription?",
    a: "Starter Kit is a one-time $49 purchase that includes 5 physical NFC tags plus your first month of Starter subscription. It's the easiest way to get started. After the first month, you'll pay $9/mo for the Starter subscription to keep your tags active. If you already have tags, you can subscribe to Starter monthly without buying the kit.",
  },
  {
    id: "faq-5",
    q: "How many tags can I have on each tier?",
    a: "Starter: Up to 25 active tags | Pro: Up to 100 active tags | Collector: Unlimited active tags. If you hit your tier's tag limit, you'll need to upgrade to activate additional tags.",
  },
  {
    id: "faq-6",
    q: "What are \'active\' tags?",
    a: "Active tags are NFC tags that are currently linked to items in your catalog and working. Deactivated or unused tags don't count toward your limit. This lets you swap tags between items or deactivate tags for items no longer on display.",
  },
  {
    id: "faq-7",
    q: "How durable are the NFC tags?",
    a: "TapTale NFC tags are rated IP68 waterproof and can withstand temperatures from -25°C to 85°C. They're printed on archival-quality materials with a minimum 10-year lifespan. The adhesive is museum-grade and won't damage surfaces.",
  },
  {
    id: "faq-8",
    q: "Can I migrate from cloud to self-hosted?",
    a: "Absolutely. We provide a one-click export tool that packages all your data in a format ready for self-hosted import. The migration wizard guides you through the process. Your NFC tags continue working without interruption during migration.",
  },
];

export default function PricingFAQ() {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="py-24 md:py-32 px-6 md:px-8 bg-bg-secondary" aria-label="Frequently asked questions">
      <div className="max-w-container mx-auto">
        <div className="text-center mb-16">
          <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em] block mb-4">
            FAQ
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight text-white">
            Questions worth
            <br />
            <span className="italic text-gray-400">answering honestly.</span>
          </h2>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs?.map((faq) => (
            <div
              key={faq?.id}
              className={`rounded-2xl border transition-all duration-300 ${
                open === faq?.id
                  ? "border-white/15 bg-bg-card" :"border-white/5 bg-bg-card hover:border-white/10"
              }`}
            >
              <button
                onClick={() => setOpen(open === faq?.id ? null : faq?.id)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
                aria-expanded={open === faq?.id}
              >
                <span className={`font-medium text-sm transition-colors ${open === faq?.id ? "text-white" : "text-gray-300"}`}>
                  {faq?.q}
                </span>
                <div className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  open === faq?.id ? "border-accent-crimson/40 bg-accent-crimson/10" : "border-white/10"
                }`}>
                  <Icon
                    name={open === faq?.id ? "MinusIcon" : "PlusIcon"}
                    size={14}
                    className={open === faq?.id ? "text-accent-crimson" : "text-gray-500"}
                  />
                </div>
              </button>

              <div className={`accordion-content ${open === faq?.id ? "open" : ""}`}>
                <div className="px-6 pb-6">
                  <div className="h-px bg-white/5 mb-4" />
                  <p className="text-gray-400 text-sm leading-relaxed">{faq?.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}